import React from "react";
import {
  Briefcase,
  MapPin,
  CalendarClock,
  BrainCircuit,
  Server,
} from "lucide-react";
import { experience } from "../mock";
import Reveal from "./Reveal";
import SectionHeader from "./SectionHeader";
import SpotlightCard from "./SpotlightCard";

const roleIcons = [BrainCircuit, Server];

const Experience = () => {
  return (
    <section id="experience" className="relative pt-0 pb-10 md:pb-12 overflow-hidden">
      <div className="orb pointer-events-none -right-40 top-1/3 h-[360px] w-[360px] bg-teal-600/15" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="04."
          eyebrow="Experience"
          title="Professional journey"
          description="Real-world roles where I've applied AI/ML engineering and scalable backend development to solve production problems."
        />

        <div className="relative ml-3 md:ml-6">
          {/* Timeline line */}
          <div className="absolute left-0 md:left-6 top-2 bottom-2 w-px bg-gradient-to-b from-primary via-emerald-500/50 to-teal-400/30" />

          <div className="space-y-8 md:space-y-10">
            {experience.map((exp, index) => {
              const Icon = roleIcons[index % roleIcons.length];
              return (
                <Reveal key={index} delay={index * 120} className="relative md:pl-20">
                  {/* Node */}
                  <div className="absolute left-0 md:left-6 top-6 md:top-8 -translate-x-1/2 md:translate-x-0 flex items-center justify-center">
                    <span className="relative grid h-7 w-7 md:h-8 md:w-8 place-items-center rounded-full bg-card border-2 border-primary shadow-lg shadow-primary/20">
                      <span className="h-2 w-2 rounded-full bg-primary" />
                      <span
                        className="absolute inset-0 rounded-full bg-primary/30 animate-ping"
                        style={{ animationDuration: "2.5s" }}
                      />
                    </span>
                  </div>

                  <SpotlightCard
                    className="cursor-target ml-10 md:ml-0 rounded-2xl border border-border bg-card/80 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5"
                    color="rgba(45,212,191,0.12)"
                  >
                    <div className="relative p-6 md:p-7">
                      <div className="absolute top-0 left-6 right-6 h-[2px] rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 scale-x-0 transition-transform duration-500 origin-left group-hover/spot:scale-x-100" />

                      {/* header row */}
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span
                          className={
                            exp.current
                              ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 text-xs font-semibold"
                              : "inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/20 px-3 py-1 text-xs font-semibold"
                          }
                        >
                          {exp.current ? (
                            <>
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                              </span>
                              Currently
                            </>
                          ) : (
                            exp.type
                          )}
                        </span>
                        <span className="font-mono text-xs md:text-sm text-muted-foreground inline-flex items-center gap-1.5">
                          <CalendarClock size={13} className="text-primary" />
                          {exp.duration}
                        </span>
                      </div>

                      {/* role row */}
                      <div className="flex items-start gap-3">
                        <span
                          className={`hidden md:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${exp.gradient} text-white shadow-md`}
                        >
                          <Icon size={20} />
                        </span>
                        <div className="min-w-0">
                          <h4 className="font-display text-lg md:text-xl font-semibold leading-snug">
                            {exp.role}
                          </h4>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5">
                              <Briefcase size={14} className="text-primary" />
                              {exp.company}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin size={14} className="text-primary" />
                              {exp.location}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* tech tags */}
                      <div className="mt-5 flex flex-wrap gap-2">
                        {exp.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 rounded-full border border-border bg-secondary/50 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors duration-200"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </SpotlightCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;